import Bluebird = require("bluebird");
import { inject, injectable } from "inversify";
import TYPES from "../TYPES";
import { ConsoleUI } from "../utils/ConsoleUI";
import { KotennaApi } from "../lib/kotenna/KotennaApi";
import { VHost } from "../lib/kotenna/VHost";
import { CloudProvider } from "../lib/kotenna/CloudProvider";
import container from "../inversify.config";
import { CloudProviderWorker } from "../providers/CloudProviderWorker";

declare global { export interface Promise<T> extends Bluebird<T> {} }
Promise = Bluebird as any;

const delay = (time) => (result) => new Promise(resolve => setTimeout(() => resolve(result), time));


@injectable()
export class VHostWorker {

    private console: ConsoleUI;
    private api: KotennaApi;

    constructor(
       @inject(TYPES.ConsoleUI) ui: ConsoleUI,
       @inject(TYPES.KotennaApi) api: KotennaApi
    ) {
        this.api = api;
        this.console = ui;
    }

    async deleteVhost(hostname: string|string[], forceDelete = false) {

        if(!hostname) {
            this.console.loaderStart("Loading VHost");
            const list = await this.api.vhost();
            const choices = list.map(v => {
                return {
                    name: `[${v.provider_key}] - ${v.node_name} - ${v.hostname}`,
                    value: v.hostname
                }
            });
            hostname = await this.console.promptCheckbox("Choose which VHost to delete", choices);
            console.log(hostname);
        }

        if(!Array.isArray(hostname)) {
            hostname = [hostname];
        }

        if(!forceDelete) {
            const go = await this.console.promptConfirm(`Deleting VHost ${hostname} (the Cloud Instance will NOT be affected in any way) - Confirm?`);
            if(!go) {
                throw new Error ("User cancelled");
            }
        }

        return Promise.resolve(hostname.map(name => this.api.vhostDelete(name)));
    }

    async updateVhost(hostname: string, change: string): Promise<VHost> {

        this.console.loaderStart("Loading VHost");
        const list = await this.api.vhost();

        const vhost = list.filter(v => v.hostname === hostname)[0];

        if(!vhost) {
            return Promise.reject(`VHost ${hostname} not found`);
        }

        const tmp = change.split("=");
        const path = tmp[0];
        const value = tmp[1].replace(/"/g, '');

        // source: https://stackoverflow.com/questions/18936915/dynamically-set-property-of-nested-object
        function set(path, value, obj) {
            let schema = obj;  // a moving reference to internal objects within obj
            const pList = path.split('.');
            const len = pList.length;
            for(let i = 0; i < len - 1; i++) {
                const elem = pList[i];
                if( !schema[elem] ) schema[elem] = {}
                schema = schema[elem];
            }
            schema[pList[len - 1]] = value;
        }

        set(path, value, vhost);

        this.console.loaderStart("Updating VHost");
        return this.api.vhostSave(vhost);

    }

    async stopVhost(hostname: string|null): Promise<string> {

        if(!hostname) {
            this.console.loaderStart("Loading VHost");
            const list = await this.api.vhost();
            hostname = await this.console.promptList("Choose the VHost to stop", list.map(v => {
                return {value: v.hostname, name: `${v.node_name} [${v.hostname}]` };
            }));
        }

        this.console.loaderStart(`Halting ${hostname}`);
        return this.api.vhostStop(hostname!);
    }


    listVhost(): Promise<VHost[]|Error> {
        this.console.loaderStart("Loading VHost");
        return this.api.vhost();
    }

    async createVhost(): Promise<VHost> {


        // 1) Choose a provider, first by choosing a flavour
        this.console.loaderStart("Loading CloudProviders");
        const providers = await this.api.providers();
        const providersByFlavour = groupByApi(providers);
        const flavour = await this.console.promptList("Choose a flavour", Object.keys(providersByFlavour));
        const choosenProviderName = await this.console.promptList("Choose a CloudProvider", Object.keys(providersByFlavour[flavour]));

        const provider: CloudProvider = providers.find(p => p.name === choosenProviderName)!;

        // 2) Get the running instances, and convert them to a tpl
        this.console.loaderStart("Loading available instances");
        const instances = await this.api.instances(provider.name);
        const instance = await this.console.promptList("Choose an instance", instances.map((i, index) => { return { value: i, name: i.name }; }));
        this.console.loaderStart(`Importing ${instance.id}`);
        const tpl = await this.api.instanceTpl(provider.name, instance.id);

        let  hostname = await this.console.promptInput("Please specify a domain name for the VHost");
        hostname = hostname.replace(/http(s?):\/\//,'');

        this.console.log(`Creating VHost from ${provider.api}/${provider.name} - ${instance.name} with FQDN ${hostname}`);

        // 3) Invoke the Cloud provider API to validate and generate the VHost
        const worker = container.get<CloudProviderWorker>(TYPES.CloudProvidertWorker(flavour));
        const vhost = await worker.createVhost(hostname, instance, provider, tpl.tpl);
        this.console.loaderStart("VHost is cooking...");
        return this.api.vhostSave(vhost);

    }


}

function groupByApi(providers: CloudProvider[]): any {
    return providers.reduce((acc, prov) => {
        if(!acc[prov.api]) {
            acc[prov.api] = {};
        }
        acc[prov.api][prov.name] = prov;
        return acc;
    }, {});
}